"use client";

import { memo, useEffect } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "@/lib/i18n";

/* Deep-link scroll helper.
 *
 * framer-motion's `whileInView` starts cards hidden with translate-y,
 * which means when the browser handles a #testimonial-* hash the final
 * layout height is not known yet and the browser lands on the wrong
 * position. We listen for hashchange + run once on mount, wait for the
 * target element to exist, then scroll programmatically. */
function useTestimonialDeepLink(): void {
  useEffect(() => {
    function scrollToHash(): void {
      if (typeof window === "undefined") return;
      const hash = window.location.hash;
      if (!hash || !hash.startsWith("#testimonial-")) return;
      const id = hash.slice(1);
      const el = document.getElementById(id);
      if (!el) return;
      // Two rAFs so framer-motion has applied its enter state,
      // then a small settle delay for any late layout shifts.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.setTimeout(() => {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 200);
        });
      });
    }
    scrollToHash();
    window.addEventListener("hashchange", scrollToHash);
    return () => window.removeEventListener("hashchange", scrollToHash);
  }, []);
}

/* ── Partner testimonials ──
 *
 * Sign-off log (do NOT add fabricated or unapproved entries):
 *  - team-telecom-davit-hayrapetyan: quote approved verbatim by email
 *    on 2026-04-22 ("we are more than OK to place this info at your website");
 *    placement + wording + attribution confirmed on 2026-04-22.
 *  - ucom-karine-yeghiazaryan: quote approved verbatim by email on
 *    2026-04-22.
 *  - cogent-mark-harris: quote approved verbatim on 2026-04-22 by Jocelyn
 *    Johnson (Sr. Marketing Communications Manager, Cogent Communications)
 *    — note: Cogent requested attribution be re-assigned from Mark Stuart
 *    (Sales Director UK & Ireland) to Mark Harris (Chief Revenue Officer
 *    & VP of Global Sales); quote body unchanged from submitted draft.
 *  - exa-radivoje-trandafir: quote approved verbatim by email on
 *    2026-04-23 by Radivoje Trandafir (VP Sales, CEE & ME,
 *    EXA Infrastructure).
 *  - caucasus-online-revaz-kopaladze: quote approved verbatim by email
 *    on 2026-04-23 by Revaz Kopaladze (CEO, Caucasus Online) —
 *    "Yes, sure, this word works for me because it is true." Original
 *    CEO-to-CEO reference request sent 2026-04-22 from
 *    nushev@sofia-connect.net to r.kopaladze@co.ge.
 *  - a1-mario-uglyarov: Bulgarian-language quote confirmed by email on
 *    2026-04-24 08:39 UTC by Mario Uglyarov (Manager Wholesale,
 *    Strategic ICT Projects, Innovations and Wholesale, A1 Bulgaria):
 *    "Потвърждавам долната референция от мое име." Mario
 *    returned an edited version (adding "в срок" / "on schedule" to
 *    the delivery-commitments clause). English rendering published on
 *    the site is a faithful translation of that final Bulgarian text.
 *    Original bilingual request sent 2026-04-22 from
 *    nushev@sofia-connect.net to Mario.Uglyarov@a1.bg.
 */
interface Testimonial {
  id: string;
  quote: string;
  author: string;
  title: string;
  company: string;
  companyUrl?: string;
}

const TESTIMONIALS: Testimonial[] = [
  {
    id: "caucasus-online-revaz-kopaladze",
    quote:
      "Caucasus Online and Sofia Connect share a strategic vision for the Digital Silk Road between Europe and the Caucasus. Our partnership around the Caucasus Cable System landing at Balchik gives European and Caucasian networks a resilient alternative to traditional transit routes. Sofia Connect's team operates with the technical rigour and commercial seriousness this corridor requires — they are exactly the kind of partner this region needs.",
    author: "Revaz Kopaladze",
    title: "CEO",
    company: "Caucasus Online",
    companyUrl: "https://co.ge",
  },
  {
    id: "a1-mario-uglyarov",
    quote:
      "Sofia Connect is a reliable partner for wholesale capacity and ICT solutions in the region. Their team is competent, communication is fast and to the point, and technical commitments are delivered on schedule. They add real value to our strategic projects across South-East Europe.",
    author: "Mario Uglyarov",
    title: "Manager Wholesale, Strategic ICT Projects, Innovations and Wholesale",
    company: "A1 Bulgaria",
    companyUrl: "https://www.a1.bg",
  },
  // Re-published 2026-05-23 (previously hidden 2026-04-23 → 2026-05-23).
  // Quotes remain approved verbatim per sign-off log above.
  {
    id: "team-telecom-davit-hayrapetyan",
    quote:
      "Sofia Connect has been a reliable partner for TEAM Telecom across multiple fronts — from European DWDM capacity to the wider regional connectivity we build together. Their engineering team is sharp, provisioning is on-time, and they run their network to a standard we're comfortable recommending. A professional carrier to do business with.",
    author: "Davit Hayrapetyan",
    title: "Head of International Carrier Relations and Wholesale Business Division",
    company: "TEAM Telecom Armenia",
    companyUrl: "https://telecomarmenia.am",
  },
  {
    id: "ucom-karine-yeghiazaryan",
    quote:
      "Sofia Connect has been a reliable wholesale partner for Ucom on the Armenia–Europe route. Their engineering team is professional, provisioning is delivered on schedule, and they consistently meet SLA commitments. A straightforward carrier to work with — which is especially valuable in our region.",
    author: "Karine Yeghiazaryan",
    title: "Head of International Carrier Relations and Wholesale Business Department",
    company: "Ucom",
    companyUrl: "https://www.ucom.am",
  },
  {
    id: "cogent-mark-harris",
    quote:
      "Sofia Connect is a solid, well-run carrier in South-East Europe. Their team is professional, their network behaves as advertised, and they're easy to do business with. We've found them to be a reliable partner in the region.",
    author: "Mark Harris",
    title: "Chief Revenue Officer & VP of Global Sales",
    company: "Cogent Communications",
    companyUrl: "https://www.cogentco.com",
  },
  {
    id: "exa-radivoje-trandafir",
    quote:
      "Sofia Connect has been a dependable and valued partner for EXA Infrastructure in South-East Europe for many years. Their team delivers on its commitments, their NOC is very responsive and highly professional, and they maintain their network to a standard we are confident recommending. They are among the carriers raising the bar for engineering discipline in the region.",
    author: "Radivoje Trandafir",
    title: "VP Sales, CEE & ME",
    company: "EXA Infrastructure",
    companyUrl: "https://exainfra.net",
  },
];

export const Testimonials = memo(function Testimonials() {
  useTestimonialDeepLink();
  const { t } = useTranslation();
  return (
    <section
      id="testimonials"
      className="py-20 px-4 sm:px-6 lg:px-8 bg-background relative overflow-hidden"
    >
      {/* Subtle top/bottom border */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />

      <div className="mx-auto max-w-5xl relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span
            className="font-mono text-[11px] sm:text-[12px] lowercase text-eyebrow"
            style={{ letterSpacing: "0.08em" }}
          >
            {t("testimonials_eyebrow")}
          </span>
          <h2
            className="font-display mt-3 text-3xl sm:text-4xl tracking-tight text-foreground"
            style={{ fontWeight: 600 }}
          >
            {t("testimonials_heading")}
          </h2>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
            {t("testimonials_subtitle")}
          </p>
        </motion.div>

        {/* Testimonial cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {TESTIMONIALS.map((item, i) => (
            <motion.figure
              key={item.id}
              id={`testimonial-${item.id}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
              className="bento-card relative scroll-mt-24 target:ring-2 target:ring-accent target:ring-offset-4 target:ring-offset-background"
            >
              {/* Decorative quote mark */}
              <span
                aria-hidden
                className="absolute top-5 right-6 text-6xl leading-none text-accent/20 font-serif select-none"
              >
                &ldquo;
              </span>

              <blockquote className="relative z-10">
                <p className="text-base sm:text-lg text-foreground leading-relaxed">
                  {item.quote}
                </p>
              </blockquote>

              <figcaption className="mt-6 flex items-center gap-4 relative z-10">
                <div
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent/10 text-accent font-semibold"
                  aria-hidden
                >
                  {item.author
                    .split(" ")
                    .map((n) => n[0])
                    .slice(0, 2)
                    .join("")}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-foreground">
                    {item.author}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {item.title}
                    {" — "}
                    {item.companyUrl ? (
                      <a
                        href={item.companyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-eyebrow underline underline-offset-2 hover:opacity-80"
                      >
                        {item.company}
                      </a>
                    ) : (
                      <span>{item.company}</span>
                    )}
                  </div>
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
});
Testimonials.displayName = "Testimonials";
